import { useState, useEffect, useRef } from "react";
import { Plus, Pencil, Trash2, Upload, X, Image } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const statusClass = { Live: "status-live", MVP: "status-mvp", Idea: "status-idea" };
const categories = ["Infrastructure", "Protocol", "Ecosystem", "Agency", "Wealth", "AI", "AI Finance"];
const statuses = ["Idea", "MVP", "Live"];

const emptyProject = {
  name: "", tagline: "", url: "", category: "Infrastructure", status: "Idea",
  description: "", features: [], api_docs: "", market_analysis: "",
  team: [], screenshots: [], extended_docs: "", brand_color: "#2563EB",
  logo: "", background_image: "",
};

function ImageUploader({ label, value, onChange, api, testId }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Label className="font-sans text-sm">{label}</Label>
      <div className="mt-1 flex items-center gap-2">
        {value ? (
          <div className="relative group">
            <img
              src={`${BACKEND}${value}`}
              alt={label}
              className="w-16 h-16 rounded-lg object-cover border border-slate-200"
            />
            <button
              onClick={() => onChange("")}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div
            onClick={() => fileRef.current?.click()}
            className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-blue-400 transition-colors"
          >
            {uploading ? (
              <div className="spinner w-5 h-5"></div>
            ) : (
              <Image className="w-5 h-5 text-slate-400" />
            )}
          </div>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="text-xs"
          data-testid={testId}
        >
          <Upload className="w-3 h-3 mr-1" /> {uploading ? "Uploading..." : "Upload"}
        </Button>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleUpload}
      />
    </div>
  );
}

function ScreenshotsUploader({ screenshots, onChange, api }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange([...screenshots, res.data.url]);
      toast.success("Screenshot uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeScreenshot = (index) => {
    onChange(screenshots.filter((_, i) => i !== index));
  };

  return (
    <div>
      <Label className="font-sans text-sm">Screenshots</Label>
      <div className="mt-1 flex flex-wrap gap-2">
        {screenshots.map((url, i) => (
          <div key={i} className="relative group">
            <img
              src={`${BACKEND}${url}`}
              alt={`Screenshot ${i + 1}`}
              className="w-20 h-14 rounded-lg object-cover border border-slate-200"
            />
            <button
              onClick={() => removeScreenshot(i)}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        <div
          onClick={() => fileRef.current?.click()}
          className="w-20 h-14 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-blue-400 transition-colors"
        >
          {uploading ? <div className="spinner w-4 h-4"></div> : <Plus className="w-4 h-4 text-slate-400" />}
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
    </div>
  );
}

export default function AdminProjects() {
  const { api } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProject);
  const [featuresText, setFeaturesText] = useState("");
  const [teamText, setTeamText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchProjects = () => {
    api.get("/projects")
      .then((r) => setProjects(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchProjects(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => {
    setForm(emptyProject);
    setFeaturesText("");
    setTeamText("");
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (project) => {
    setForm({ ...emptyProject, ...project });
    setFeaturesText((project.features || []).join(", "));
    setTeamText((project.team || []).map(t => `${t.name}:${t.role}`).join(", "));
    setEditing(project.project_id);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.tagline) {
      toast.error("Name and tagline are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        features: featuresText ? featuresText.split(",").map(s => s.trim()).filter(Boolean) : [],
        team: teamText ? teamText.split(",").map(s => {
          const parts = s.trim().split(":");
          return { name: parts[0]?.trim() || "", role: parts[1]?.trim() || "" };
        }).filter(t => t.name) : [],
      };

      if (editing) {
        await api.put(`/projects/${editing}`, payload);
        toast.success("Project updated");
      } else {
        await api.post("/projects", payload);
        toast.success("Project created");
      }
      setShowForm(false);
      fetchProjects();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save project");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await api.delete(`/projects/${projectId}`);
      toast.success("Project deleted");
      fetchProjects();
    } catch (e) {
      toast.error("Failed to delete project");
    }
  };

  return (
    <div data-testid="admin-projects-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-mono text-xs tracking-[0.25em] uppercase text-slate-400 mb-2">Management</p>
          <h1 className="font-serif text-3xl font-bold text-slate-900 tracking-tight">Projects</h1>
        </div>
        <Button onClick={openNew} className="bg-slate-900 text-white hover:bg-gradient-to-r hover:from-blue-600 hover:to-cyan-400 font-sans gap-2" data-testid="admin-new-project-btn">
          <Plus className="w-4 h-4" /> New Project
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="spinner"></div></div>
      ) : (
        <div className="space-y-3">
          {projects.map((project) => (
            <div key={project.project_id} className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4" data-testid={`admin-project-${project.project_id}`}>
              {/* Logo preview */}
              <div className="w-10 h-10 rounded-lg border border-slate-200 overflow-hidden flex items-center justify-center bg-slate-50 flex-shrink-0">
                {project.logo ? (
                  <img src={`${BACKEND}${project.logo}`} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full" style={{ backgroundColor: project.brand_color + "20" }}></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-sans text-base font-medium text-slate-900 truncate">{project.name}</h3>
                  <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${statusClass[project.status]}`}>{project.status}</span>
                  <span className="font-mono text-[10px] tracking-wider uppercase text-slate-400">{project.category}</span>
                </div>
                <p className="font-sans text-sm text-slate-500 truncate">{project.tagline}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => openEdit(project)} className="text-slate-400 hover:text-blue-600" data-testid={`edit-project-${project.project_id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(project.project_id)} className="text-slate-400 hover:text-red-500" data-testid={`delete-project-${project.project_id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Project Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="project-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing ? "Edit Project" : "New Project"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="font-sans text-sm">Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1" data-testid="project-name-input" />
              </div>
              <div>
                <Label className="font-sans text-sm">URL</Label>
                <Input value={form.url} onChange={(e) => setForm({...form, url: e.target.value})} placeholder="https://..." className="mt-1" data-testid="project-url-input" />
              </div>
            </div>
            <div>
              <Label className="font-sans text-sm">Tagline *</Label>
              <Input value={form.tagline} onChange={(e) => setForm({...form, tagline: e.target.value})} className="mt-1" data-testid="project-tagline-input" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="font-sans text-sm">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger className="mt-1" data-testid="project-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-sans text-sm">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                  <SelectTrigger className="mt-1" data-testid="project-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-sans text-sm">Brand Color</Label>
                <div className="flex gap-2 mt-1">
                  <input type="color" value={form.brand_color || "#2563EB"} onChange={(e) => setForm({...form, brand_color: e.target.value})} className="w-10 h-10 rounded border cursor-pointer" data-testid="project-color-input" />
                  <Input value={form.brand_color} onChange={(e) => setForm({...form, brand_color: e.target.value})} className="flex-1" />
                </div>
              </div>
            </div>

            {/* Image uploads */}
            <div className="section-gradient-line"></div>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-slate-400">Media</p>
            <div className="grid grid-cols-2 gap-4">
              <ImageUploader label="Logo" value={form.logo} onChange={(v) => setForm({...form, logo: v})} api={api} testId="upload-logo-btn" />
              <ImageUploader label="Background Image" value={form.background_image} onChange={(v) => setForm({...form, background_image: v})} api={api} testId="upload-bg-btn" />
            </div>
            <ScreenshotsUploader screenshots={form.screenshots || []} onChange={(v) => setForm({...form, screenshots: v})} api={api} />

            <div className="section-gradient-line"></div>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-slate-400">Content</p>
            <div>
              <Label className="font-sans text-sm">Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1 min-h-[100px]" data-testid="project-description-input" />
            </div>
            <div>
              <Label className="font-sans text-sm">Features (comma-separated)</Label>
              <Input value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} placeholder="Feature 1, Feature 2, ..." className="mt-1" data-testid="project-features-input" />
            </div>
            <div>
              <Label className="font-sans text-sm">Team (format: Name:Role, comma-separated)</Label>
              <Input value={teamText} onChange={(e) => setTeamText(e.target.value)} placeholder="John Doe:CEO, Jane Smith:CTO" className="mt-1" data-testid="project-team-input" />
            </div>
            <div>
              <Label className="font-sans text-sm">API Documentation</Label>
              <Textarea value={form.api_docs} onChange={(e) => setForm({...form, api_docs: e.target.value})} className="mt-1" data-testid="project-apidocs-input" />
            </div>
            <div>
              <Label className="font-sans text-sm">Market Analysis</Label>
              <Textarea value={form.market_analysis} onChange={(e) => setForm({...form, market_analysis: e.target.value})} className="mt-1" data-testid="project-market-input" />
            </div>
            <div>
              <Label className="font-sans text-sm">Extended Docs (visible to accepted members only)</Label>
              <Textarea value={form.extended_docs} onChange={(e) => setForm({...form, extended_docs: e.target.value})} className="mt-1" data-testid="project-extdocs-input" />
            </div>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full bg-slate-900 text-white hover:bg-gradient-to-r hover:from-blue-600 hover:to-cyan-400 font-sans"
              data-testid="project-submit-btn"
            >
              {submitting ? "Saving..." : editing ? "Update Project" : "Create Project"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
